/** @jsxImportSource @emotion/react */
import React from 'react'
import { css, keyframes } from '@emotion/react'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'

export interface NavigationTab {
  id: string
  title: string
  subtitle: string
  status: 'ready' | 'pending' | 'incomplete'
  disabled?: boolean
  disabledReason?: string
}

interface SchedulerNavigationBarProps {
  tabs: NavigationTab[]
  activeTabId: string
  onTabChange: (tabId: string) => void
  loading?: boolean
  /** When true, removes horizontal padding (parent provides it) */
  embedded?: boolean
  // Unified nav bar props
  subTabsControl?: React.ReactNode
  onGenerate?: () => void
  generateLabel?: string
  generateDisabled?: boolean
  generateDisabledReason?: string
  generateIssueCount?: number
  onPublish?: () => void
  showPublish?: boolean
  onUpload?: () => void
  onExport?: () => void
  onMoreOptions?: () => void
  onTourRestart?: () => void
}

/** Checkmark icon for completed steps */
// @ts-ignore TS6133 - CheckmarkIcon IS used in JSX below (line ~320)
const CheckmarkIcon: React.FC = () => (
  <svg
    width='12'
    height='12'
    viewBox='0 0 12 12'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M10 3L4.5 8.5L2 6'
      stroke={colors.BLACK}
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/** Chevron separator between steps */
const ChevronSeparator: React.FC = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    css={css`
      flex-shrink: 0;
      opacity: 0.4;
    `}
  >
    <path
      d='M6 4L10 8L6 12'
      stroke={colors.WHITE}
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const c = colors as Record<string, string>

const SchedulerNavigationBar: React.FC<SchedulerNavigationBarProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  loading,
  embedded,
  subTabsControl,
  onGenerate,
  generateLabel = 'Generate Schedule',
  generateDisabled,
  generateDisabledReason,
  generateIssueCount = 0,
  onPublish,
  showPublish,
  onUpload,
  onExport,
  onMoreOptions,
  onTourRestart,
}) => {
  const containerStyles = css`
    padding: ${embedded ? '8px 0 16px' : '0 88px'};
    border-bottom: 1px solid ${colors.SECONDARY_200};
    ${embedded
      ? 'margin-top: 16px;'
      : `background-color: ${colors.SECONDARY_400};`}
  `

  const innerContainerStyles = css`
    max-width: 1440px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    @media (max-width: 1400px) {
      gap: 10px;
    }
  `

  const leftSideStyles = css`
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
    flex: 1;

    @media (max-width: 1400px) {
      gap: 10px;
    }
  `

  // Stepper container — scrolls horizontally on narrow viewports
  const stepperStyles = css`
    display: flex;
    align-items: center;
    gap: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `

  // Step chip styles
  const getStepStyles = (isActive: boolean, isDisabled?: boolean) => css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0.8px 12.8px;
    height: 33.6px;
    border: 0.8px solid ${isActive ? colors.PRIMARY_300 : c.SECONDARY_125};
    border-radius: 8px;
    background: ${isActive ? 'rgba(242, 180, 0, 0.1)' : 'transparent'};
    cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;
    white-space: nowrap;

    @media (max-width: 1400px) {
      padding: 0.8px 8px;
      gap: 6px;
    }

    &:hover {
      ${!isActive && !isDisabled ? `border-color: ${colors.NEUTRAL_80};` : ''}
    }
  `

  // Step number badge
  const getStepBadgeStyles = (isGold: boolean) => css`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${isGold ? colors.PRIMARY_300 : c.SECONDARY_125};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `

  const getStepBadgeTextStyles = (isActive: boolean) => css`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: ${isActive ? colors.BLACK : colors.WHITE};
    margin: 0;
  `

  // Step label
  const getStepLabelStyles = (isActive: boolean) => css`
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${isActive ? colors.PRIMARY_300 : colors.WHITE};
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 1400px) {
      font-size: 13px;
      max-width: 120px;
    }

    @media (max-width: 1200px) {
      max-width: 90px;
    }
  `

  // Shimmer placeholder for loading
  const shimmer = keyframes`
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  `

  const tabShimmerStyles = css`
    height: 14px;
    width: 80px;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      ${colors.SECONDARY_200} 25%,
      ${colors.SECONDARY_100} 50%,
      ${colors.SECONDARY_200} 75%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s ease-in-out infinite;
  `

  // Right side: action buttons
  const rightSideStyles = css`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
  `

  // Generate button: gold bg with dark text
  const generateBtnStyles = css`
    padding: 0 12px;
    height: 33.6px;
    background-color: ${colors.PRIMARY_300};
    border: none;
    color: ${colors.SECONDARY_500};
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    flex-shrink: 0;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background-color: ${colors.PRIMARY_200};
    }

    @media (max-width: 1400px) {
      padding: 0 10px;
      font-size: 13px;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  `

  // Primary action button: gold bg, dark text (used for Publish)
  const primaryActionBtnStyles = css`
    padding: 8px 16px;
    height: 32px;
    background-color: ${colors.PRIMARY_300};
    border: none;
    color: ${colors.SECONDARY_300};
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    flex-shrink: 0;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background-color: ${colors.PRIMARY_200};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  `

  // Icon button: outlined gold border
  const iconButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: transparent;
    border: 1px solid ${colors.PRIMARY_300};
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(242, 180, 0, 0.1);
    }
  `

  const iconWrapperStyles = css`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  `

  return (
    <nav css={containerStyles}>
      <div css={innerContainerStyles}>
        {/* Left side: subTabsControl + stepper steps */}
        <div css={leftSideStyles}>
          {subTabsControl}
          <div css={stepperStyles} data-tour-stepper>
            {tabs.map((tab, index) => {
              const isActive = tab.id === activeTabId
              const isCompleted = tab.status === 'ready' && !isActive
              const stepNumber = index + 1
              return (
                <React.Fragment key={tab.id}>
                  {index > 0 && <ChevronSeparator />}
                  <Tooltip
                    title={
                      tab.disabled && tab.disabledReason
                        ? tab.disabledReason
                        : undefined
                    }
                    placement='bottom'
                  >
                    <span
                      css={css`
                        display: inline-flex;
                      `}
                    >
                      <button
                        type='button'
                        css={getStepStyles(isActive, tab.disabled)}
                        onClick={() => !tab.disabled && onTabChange(tab.id)}
                        aria-selected={isActive}
                        aria-disabled={tab.disabled}
                        role='tab'
                        data-tour-step={tab.id}
                      >
                        <div css={getStepBadgeStyles(isActive || isCompleted)}>
                          {isCompleted ? (
                            <CheckmarkIcon />
                          ) : (
                            <span css={getStepBadgeTextStyles(isActive)}>
                              {stepNumber}
                            </span>
                          )}
                        </div>
                        {loading ? (
                          <div css={tabShimmerStyles} />
                        ) : (
                          <span css={getStepLabelStyles(isActive)}>
                            {tab.title}
                          </span>
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </React.Fragment>
              )
            })}
          </div>
        </div>

        {/* Right side: action buttons */}
        <div css={rightSideStyles}>
          {onGenerate && (
            <Tooltip
              title={
                generateDisabled && generateDisabledReason
                  ? generateDisabledReason
                  : undefined
              }
              placement='bottom'
            >
              <span
                css={css`
                  display: inline-flex;
                `}
              >
                <button
                  type='button'
                  css={generateBtnStyles}
                  onClick={onGenerate}
                  disabled={generateDisabled}
                  aria-label='Generate schedule'
                  data-tour-generate-btn
                >
                  <Icon
                    name='magic'
                    fontSize={14}
                    color={colors.SECONDARY_500}
                  />
                  {generateLabel}
                  {generateIssueCount > 0 && (
                    <span
                      css={css`
                        background-color: ${colors.ERROR_200};
                        color: ${colors.WHITE};
                        font-size: 12px;
                        font-weight: 600;
                        min-width: 18px;
                        height: 18px;
                        border-radius: 9px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0 5px;
                        margin-left: 4px;
                      `}
                    >
                      {generateIssueCount}
                    </span>
                  )}
                </button>
              </span>
            </Tooltip>
          )}
          {showPublish && onPublish && (
            <button
              type='button'
              css={primaryActionBtnStyles}
              onClick={onPublish}
              aria-label='Publish schedule'
            >
              <Icon name='magic' fontSize={14} color={colors.SECONDARY_300} />
              Publish
            </button>
          )}
          {onUpload && (
            <button
              type='button'
              css={iconButtonStyles}
              onClick={onUpload}
              aria-label='Upload schedule'
            >
              <div css={iconWrapperStyles}>
                <Icon name='upload' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          )}
          {onExport && (
            <button
              type='button'
              css={iconButtonStyles}
              onClick={onExport}
              aria-label='Download schedule'
            >
              <div css={iconWrapperStyles}>
                <Icon name='download' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          )}
          {/* More options button — always shown */}
          {onMoreOptions && (
            <button
              type='button'
              css={iconButtonStyles}
              onClick={onMoreOptions}
              aria-label='More options'
            >
              <div css={iconWrapperStyles}>
                <Icon name='ellipsis-v' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          )}
          <Tooltip title='Schedule Creation Help' placement='bottom'>
            <button
              type='button'
              onClick={onTourRestart}
              css={iconButtonStyles}
              aria-label='Schedule creation help'
            >
              <div css={iconWrapperStyles}>
                <Icon name='info-circle' fontSize={16} color={colors.WHITE} />
              </div>
            </button>
          </Tooltip>
        </div>
      </div>
    </nav>
  )
}

export default SchedulerNavigationBar
